// Fase 4 — Vlog-scripts (B1-Nederlandse labels)
import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import api from "@/lib/api";
import { Loader2, Sparkles, Play, DollarSign, ChevronDown, ChevronRight } from "lucide-react";


const fmtEUR = (n) => (n == null ? "—" : `€ ${Number(n).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtUSD = (n) => (n == null ? "—" : `$ ${Number(n).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

// UI-mapping van interne statussen naar B1-Nederlands.
const STATUS_NL = {
  DRAFT: "Concept",
  APPROVED_FOR_RENDER: "Goedgekeurd voor video",
  RENDERING: "Video wordt gemaakt",
  DONE: "Klaar",
  FAILED: "Mislukt",
  CANCELLED: "Geannuleerd",
};


export default function VideoPage() {
  const [script, setScript] = useState(null);
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const [cost, setCost] = useState(null);
  const [ceiling, setCeiling] = useState("2.00");
  const [render, setRender] = useState(null);
  const [err, setErr] = useState(null);
  const [existing, setExisting] = useState([]);
  const [showBreakdown, setShowBreakdown] = useState(false);
  // M136 — kostenmodal-state (USD groot + EUR klein + ECB-datum).
  const [showCostModal, setShowCostModal] = useState(false);
  // M136-b (root) — health-status voor render-badge boven aan de pagina.
  const [health, setHealth] = useState(null);

  useEffect(() => {
    // M136-b — onbetaalde health-probe bij page-load; nooit een echte
    // render-call. Bepaalt render-badge + Uitvoer-knop enable/disable.
    (async () => {
      try {
        const r = await api.get("/vlogs/health");
        setHealth(r.data);
      } catch { /* stil — badge blijft neutraal */ }
    })();
  }, []);

  const loadExisting = async () => {
    try {
      const r = await api.get("/video/scripts?limit=100");
      setExisting(r.data.items || []);
    } catch (e) { /* stil */ }
  };
  useEffect(() => { loadExisting(); }, []);

  // M119 fix: klik op bestaand script laat de speler zien wanneer er al
  // een `final_video_url` of `mp4_url` op het script staat. Voorheen bleef
  // `render` null en toonde de UI géén <video>, ondanks dat de mp4 klaar
  // stond in GridFS.
  const selectExisting = (s) => {
    setScript(s);
    setCost(null);
    setErr(null);
    const backendBase = (process.env.REACT_APP_BACKEND_URL || "");
    const doneUrl = s?.result?.final_video_url
      || s?.result?.durable_url || s?.result?.mp4_url;
    if (doneUrl && (s.status === "DONE" || s.status === "RENDERING")) {
      const abs = doneUrl.startsWith("/api/")
        ? `${backendBase}${doneUrl}` : doneUrl;
      setRender({
        job_id: s.render_job_id || `SCRIPT-${s.id}`,
        status: s.status,
        mp4_url: abs,
        final_video_url: abs,
        file_id: doneUrl.split("/").pop().split("?")[0],
      });
    } else {
      setRender(null);
    }
  };

  const generate = async () => {
    if (!topic.trim()) return;
    setBusy(true); setErr(null); setCost(null); setRender(null);
    try {
      const r = await api.post("/video/script/generate",
        { topic_nl: topic, target_channel: "tiktok", duration_seconds: 15 });
      setScript(r.data);
      loadExisting();
    } catch (e) {
      const nl = e?.response?.data?.detail?.error_nl
        || e?.response?.data?.error_nl
        || "JustEasySEO kon dit script nu niet opstellen. Probeer het over een paar seconden opnieuw.";
      setErr(nl);
    }
    finally { setBusy(false); }
  };

  const estimate = async () => {
    if (!script) return;
    setBusy(true);
    try {
      // M136 — nieuwe kostenraming-endpoint met ECB EUR-koers.
      const r = await api.get(`/vlogs/${script.id}/cost-estimate`,
        { params: { resolution: "720p", dynamic_range: "SDR" } });
      setCost(r.data);
      setShowCostModal(true);
    } catch (e) { setErr(e?.response?.data?.detail || e?.message); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    if (!script) return;
    setBusy(true);
    try {
      const r = await api.post(`/video/script/${script.id}/approve`,
        { approved_budget_ceiling_eur: parseFloat(ceiling) });
      setScript(r.data);
    } catch (e) { setErr(e?.response?.data?.detail?.error_nl || e?.message); }
    finally { setBusy(false); }
  };

  const doRender = async () => {
    if (!script) return;
    setBusy(true);
    try {
      const r = await api.post(`/video/script/${script.id}/render`);
      setRender(r.data);
    } catch (e) { setErr(e?.response?.data?.detail?.error_nl || e?.message); }
    finally { setBusy(false); }
  };

  const pollStatus = async () => {
    if (!render?.job_id) return;
    setBusy(true);
    try {
      const r = await api.get(`/video/render/${render.job_id}/status`);
      setRender({ ...render, ...r.data });
    } catch (e) { setErr(e?.response?.data?.detail?.error_nl || e?.message); }
    finally { setBusy(false); }
  };

  const patchScene = async (idx, field, value) => {
    if (!script) return;
    const scenes = script.scenes.map((s, i) => i === idx ? { ...s, [field]: value } : s);
    const r = await api.patch(`/video/script/${script.id}`, { scenes });
    setScript(r.data);
  };

  const totalDur = (script?.scenes || []).reduce((s, x) => s + Number(x.duration_s || 0), 0);
  // M136-c (root) — Goedkeuren is een interne actie zonder kosten;
  // knop is ALTIJD beschikbaar zolang script in DRAFT staat, ook als
  // de videodienst nog niet actief is. Alleen "Video maken" (betaalde
  // render) blokkeren we bij niet-reachable status.
  const canApprove = script && script.status === "DRAFT";
  // M136-b — kostenraming (a): werkende raming = `cost.available` + EUR bekend.
  const costAvailable = !!(cost && cost.available);
  const eurKnown = !!(cost && cost.rate_available && cost.eur_amount != null);
  const usdKnown = !!(cost && cost.usd_amount != null);
  // M136-b — (b) render technisch mogelijk = key + reachable (onbetaalde probe).
  const renderReachable = !!(health && health.luma_key_present && health.luma_reachable);
  const canRender = script && script.status === "APPROVED_FOR_RENDER"
                       && eurKnown && renderReachable;

  return (
    <AppLayout crumb="Vlogs">
      <div data-testid="video-page" className="max-w-[900px] mx-auto px-4 py-6">
        <h1 className="text-2xl font-semibold text-text-primary mb-1" data-testid="video-title">
          AI-vlogscripts
        </h1>
        <p className="text-sm text-text-muted mb-4">
          Maak een script met scenes, keur goed en laat JustEasySEO de video maken — allemaal in adviesmodus.
          Betaalde video maken alleen als officiële prijs bevestigd is.
        </p>

        {err && <div data-testid="video-error" className="p-3 rounded border border-neg-red/40 bg-neg-red/10 text-xs text-neg-red mb-3">{err}</div>}

        {/* M136-b/c — render-status badge; klanttekst-only, geen tech-detail. */}
        {health && (
          <div data-testid="vlog-render-badge"
                className={`mb-3 rounded-md border px-3 py-2 text-[11px] flex items-center gap-2 ${ renderReachable ? "border-pos-green/40 bg-pos-green/10 text-pos-green" : "border-warn-amber/40 bg-warn-amber/10 text-warn-amber"}`}>
            <span className={`inline-block w-2 h-2 rounded-full ${ renderReachable ? "bg-pos-green" : "bg-warn-amber"}`} />
            <span className="font-semibold">
              {renderReachable
                ? "Video maken is klaar voor goedkeuring"
                : "Video maken is nog niet beschikbaar. JustEasySEO moet de videodienst nog activeren."}
            </span>
          </div>
        )}

        {existing.length > 0 && (
          <div data-testid="video-existing-list" className="rounded-md border border-border-hairline bg-bg-panel p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold">Bestaande scripts ({existing.length})</h2>
              <button onClick={loadExisting} className="text-[10px] text-accent-teal hover:text-accent-teal-dim">
                Vernieuwen
              </button>
            </div>
            <ul className="space-y-1">
              {existing.slice(0, 15).map((s) => (
                <li key={s.id} data-testid={`video-existing-${s.id}`}
                     className="flex items-center justify-between gap-2 py-1 border-b border-border-hairline/60 last:border-0">
                  <button type="button" onClick={() => selectExisting(s)}
                           data-testid={`video-existing-select-${s.id}`}
                           className="text-xs text-text-primary hover:text-accent-teal truncate max-w-[70%] text-left">
                    {s.title_nl || s.topic_nl || s.id}
                  </button>
                  <span className="text-[9px] text-info-blue border border-info-blue/40 bg-info-blue/10 px-1.5 py-0.5 rounded">
                    {STATUS_NL[s.status] || s.status}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="rounded-md border border-border-hairline bg-bg-panel p-4 mb-4">
          <label className="text-[10px] text-text-muted">Onderwerp</label>
          <input data-testid="video-topic-input" value={topic} onChange={e => setTopic(e.target.value)}
                  className="w-full mt-1 bg-bg-panel-raised border border-border-hairline rounded px-2 py-1.5 text-sm text-text-primary"
                  placeholder="Bijv. Voor/na microcement badkamer in Utrecht" />
          <button data-testid="video-generate-btn" onClick={generate} disabled={busy || !topic.trim()}
                    className="mt-2 h-9 px-4 rounded-md bg-accent-teal hover:bg-accent-teal-dim text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Concept maken
          </button>
        </div>

        {script && (
          <div className="rounded-md border border-border-hairline bg-bg-panel p-4 mb-4" data-testid="video-script">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold">{script.title_nl || script.topic_nl}</h2>
              <span className="text-[9px] text-info-blue border border-info-blue/40 bg-info-blue/10 px-1.5 py-0.5 rounded" data-testid="video-script-status">
                {STATUS_NL[script.status] || script.status}
              </span>
            </div>
            <p className="text-xs text-text-muted mb-2">Openingszin: {script.hook_5s_nl}</p>
            <div className="space-y-2">
              {(script.scenes || []).map((s, i) => (
                <div key={i} data-testid={`video-scene-${i}`} className="rounded bg-bg-panel-raised border border-border-hairline p-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[9px] text-info-blue">SCENE {i + 1}</span>
                    <input type="number" min="1" max="20" value={s.duration_s} onChange={e => patchScene(i, "duration_s", Number(e.target.value))}
                            className="w-16 bg-bg-panel border border-border-hairline rounded px-1 py-0.5 text-xs" />
                    <span className="text-[9px] text-text-muted">sec</span>
                  </div>
                  <textarea rows={2} value={s.prompt_nl || ""} onChange={e => patchScene(i, "prompt_nl", e.target.value)}
                              className="w-full bg-bg-panel border border-border-hairline rounded px-2 py-1 text-xs" placeholder="Beeldbeschrijving (NL)" />
                  <textarea rows={1} value={s.onscreen_text_nl || ""} onChange={e => patchScene(i, "onscreen_text_nl", e.target.value)}
                              className="w-full mt-1 bg-bg-panel border border-border-hairline rounded px-2 py-1 text-xs" placeholder="Tekst in beeld" />
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-[10px] text-text-muted">Totale duur: {totalDur}s</span>
              <button data-testid="video-estimate-btn" onClick={estimate} disabled={busy} className="h-8 px-3 rounded-md border border-border-hairline hover:bg-bg-panel-raised text-xs">
                <DollarSign className="w-3.5 h-3.5 inline mr-1" /> Kosten bekijken
              </button>
            </div>

            {cost && (
              <div data-testid="video-cost-panel" className="mt-3 rounded border border-border-hairline bg-bg-panel-raised p-3">
                {cost.available === false ? (
                  <div className="text-[12px] text-warn-amber italic"
                        data-testid="video-cost-unavailable">
                    {cost.reason_nl || "Kostenraming niet beschikbaar."}
                  </div>
                ) : (
                  <>
                    <div className="text-[11px] text-text-muted mb-1">
                      Uw video wordt gemaakt uit{" "}
                      <span data-testid="video-cost-clip-count" className="text-text-primary num">
                        {cost.clip_count}
                      </span>{" "}
                      clip(s) van in totaal{" "}
                      <span data-testid="video-cost-total-seconds" className="text-text-primary num">
                        {cost.total_seconds}
                      </span> seconden.
                    </div>
                    <div className={`text-sm ${eurKnown ? "text-accent-teal" : "text-warn-amber"}`}
                          data-testid="video-cost-display">
                      Kosten: <span className="font-semibold">{fmtUSD(cost.usd_amount)}</span>
                      {eurKnown ? (
                        <>{" "}(≈ <span className="font-semibold">{fmtEUR(cost.eur_amount)}</span>, ECB-koers {cost.rate_date_iso})</>
                      ) : (
                        <>{" "}<span className="text-text-muted italic">— koers tijdelijk niet beschikbaar</span></>
                      )}
                    </div>
                    {cost.per_clip?.length > 0 && (
                      <button type="button" data-testid="video-breakdown-toggle"
                               onClick={() => setShowBreakdown(v => !v)}
                               className="mt-2 text-[10px] text-accent-teal hover:text-accent-teal-dim inline-flex items-center gap-1">
                        {showBreakdown ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        {showBreakdown ? "Verberg kostenopbouw" : "Toon kostenopbouw"}
                      </button>
                    )}
                    {showBreakdown && cost.per_clip && (
                      <ul data-testid="video-breakdown-list" className="mt-2 space-y-1 text-xs text-text-primary">
                        {cost.per_clip.map((c, i) => (
                          <li key={i} className="border-l-2 border-border-hairline pl-2">
                            <span className="text-[10px] text-text-muted">
                              Clip {i + 1} · {c.seconds}s · {c.resolution} · {c.sdr_hdr}
                            </span>
                            {" — "}
                            <span>{c.usd_amount != null ? fmtUSD(c.usd_amount) : "kosten onbekend"}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-2 text-[10px] text-text-subtle">
                      Bron: <a href={cost.pricing_source_url} target="_blank" rel="noreferrer" className="underline hover:text-accent-teal">Luma-prijstabel</a>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <label className="text-[10px] text-text-muted">Plafond €</label>
              <input type="number" step="0.5" min="0" value={ceiling} onChange={e => setCeiling(e.target.value)}
                      className="w-20 bg-bg-panel-raised border border-border-hairline rounded px-2 py-1 text-xs" data-testid="video-ceiling-input" />
              <button data-testid="video-approve-btn" onClick={approve} disabled={busy || !canApprove}
                        className="h-8 px-3 rounded-md bg-pos-green hover:bg-pos-green/85 text-white text-xs font-semibold disabled:opacity-50">
                Video goedkeuren
              </button>
              <button data-testid="video-render-btn" onClick={doRender} disabled={busy || !canRender}
                        title={!renderReachable
                                ? "Video maken is nog niet beschikbaar. JustEasySEO moet de videodienst nog activeren."
                                : (!eurKnown
                                    ? "Koers tijdelijk niet beschikbaar."
                                    : "")}
                        className="h-8 px-3 rounded-md bg-accent-teal hover:bg-accent-teal-dim text-white text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
                <Play className="w-3.5 h-3.5" /> Video maken
              </button>
            </div>
          </div>
        )}

        {render && (
          <div className="rounded-md border border-border-hairline bg-bg-panel p-4" data-testid="video-render-panel">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[10px] text-text-muted">Taak</div>
                <div className="text-sm">{render.job_id}</div>
              </div>
              <span data-testid="video-render-status" className="text-[9px] text-info-blue border border-info-blue/40 bg-info-blue/10 px-1.5 py-0.5 rounded">
                {STATUS_NL[render.status] || render.status}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={pollStatus} data-testid="video-poll-btn" className="h-8 px-3 rounded-md border border-border-hairline hover:bg-bg-panel-raised text-xs">
                Status ophalen
              </button>
              {render.mp4_url && render.file_id && (
                <a href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/media/file/${render.file_id}?dl=1`}
                    data-testid="video-download-btn"
                    download
                    className="h-8 px-3 rounded-md bg-accent-teal hover:bg-accent-teal-dim text-white text-xs font-semibold inline-flex items-center gap-1.5">
                  Downloaden
                </a>
              )}
            </div>
            {render.mp4_url && (
              <video data-testid="video-player" src={render.mp4_url}
                      crossOrigin="use-credentials"
                      controls className="mt-3 max-w-[400px] rounded" />
            )}
            {render.job_id?.startsWith("SANDBOX") && (
              <p className="mt-2 text-[10px] text-warn-amber">
                Testmodus — geen echte video gemaakt
              </p>
            )}
          </div>
        )}

        {/* M136 — Kostenmodal (USD groot + EUR klein + ECB-datum + uitsluitingen) */}
        {showCostModal && cost && (
          <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
                data-testid="vlog-cost-modal"
                onClick={(e) => { if (e.target === e.currentTarget) setShowCostModal(false); }}>
            <div className="bg-bg-panel border border-border-hairline rounded-card w-full max-w-md"
                  style={{ maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
              <div className="p-5 border-b border-border-hairline shrink-0">
                <div className="text-[10px] text-text-muted">
                  Geschatte kosten voor deze video
                </div>
                <div className="text-sm text-text-primary mt-1">
                  {cost.clips_total} clip(s) — {cost.model} · {cost.resolution} · {cost.dynamic_range}
                </div>
              </div>
              <div className="p-5 overflow-y-auto" style={{ flex: "1 1 auto", minHeight: 0 }}>
                {/* USD (groot) */}
                <div className="text-[10px] text-text-muted">
                  Basisprijs (USD)
                </div>
                <div className="text-3xl font-semibold text-accent-teal tabular-nums mt-1"
                      data-testid="vlog-cost-usd">
                  {fmtUSD(cost.usd_amount)}
                </div>
                {/* EUR (klein) */}
                {cost.rate_available && cost.eur_amount != null ? (
                  <div className="mt-3">
                    <div className="text-[10px] text-text-muted">
                      Omgerekend (EUR)
                    </div>
                    <div className="text-lg text-text-primary tabular-nums"
                          data-testid="vlog-cost-eur">
                      {fmtEUR(cost.eur_amount)}
                    </div>
                    <div className="text-[10px] text-text-subtle mt-0.5"
                          data-testid="vlog-cost-rate-line">
                      ECB-referentiekoers {cost.rate_date_iso} · 1 EUR = {cost.rate_usd_per_eur} USD
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 text-[11px] text-warn-amber"
                        data-testid="vlog-cost-eur-unavailable">
                    Koers tijdelijk niet beschikbaar; probeer het later opnieuw.
                  </div>
                )}
                {/* Uitsluitingen (B1) */}
                <div className="mt-4 rounded border border-border-hairline bg-bg-panel-raised p-3">
                  <div className="text-[10px] text-text-muted mb-1">
                    Wat is niet inbegrepen
                  </div>
                  <ul className="text-[12px] text-text-primary list-disc list-inside space-y-0.5">
                    <li>BTW</li>
                    <li>Transactiekosten (bijv. van uw betaalprovider)</li>
                    <li>Platform-verhogingen bovenop de basisprijs</li>
                  </ul>
                </div>
                <div className="mt-3 text-[11px] text-text-muted italic"
                      data-testid="vlog-cost-disclaimer">
                  {cost.disclaimer_nl}
                </div>
                <div className="mt-3 text-[10px] flex flex-wrap gap-x-3 gap-y-1 text-text-subtle">
                  {cost.rate_available && (
                    <a href={cost.rate_source_url} target="_blank" rel="noreferrer"
                        className="underline hover:text-accent-teal"
                        data-testid="vlog-cost-rate-source">
                      ECB-referentiekoers
                    </a>
                  )}
                  <a href={cost.pricing_source_url} target="_blank" rel="noreferrer"
                      className="underline hover:text-accent-teal">
                    Luma-prijstabel
                  </a>
                </div>
              </div>
              <div className="p-4 border-t border-border-hairline shrink-0 flex justify-end">
                <button onClick={() => setShowCostModal(false)}
                        data-testid="vlog-cost-close"
                        className="text-[11px] px-3 py-1 rounded border border-border-hairline text-text-primary">
                  Sluiten
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </AppLayout>
  );
}
